import { inject, Injectable, Injector } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import {
  ZIMCallAcceptConfig,
  ZIMCallCancelConfig,
  ZIMCallEndConfig,
  ZIMCallInvitationMode,
  ZIMCallInviteConfig,
  ZIMCallQuitConfig,
  ZIMCallRejectConfig,
  ZIMPushConfig,
} from 'zego-zim-web';

import { MeetingManagementService } from '../../../pages/meeting-management/services/meeting-management.service';
import { BroadcastService } from '../../../services/broadcast.service';
import { HomieService } from '../../../services/homie.service';
import { ZegoMediaControlService } from '../../../services/zego-media-control.service';
import { createLogger } from '../../../utils/logger.util';
import { getUserInfo } from '../../../utils/utils';
import { CALL_ACTION, CALL_END_REASON, CALL_STATE, CALL_TYPE } from '../constants';
import { CallType, SwitchCallExtendedData } from '../types/call';
import { CallStoreService } from './call-store.service';
import { CallZegoService } from './call-zego.service';
import { CallZimService } from './call-zim.service';

@Injectable({ providedIn: 'root' })
export class CallLifecycleService {
  private readonly logger = createLogger('Call - CallLifecycleService');
  private injector = inject(Injector);
  private meetingManagementService = inject(MeetingManagementService);
  private callZimService = inject(CallZimService);
  private callZegoService = inject(CallZegoService);
  private zegoMediaControlService = inject(ZegoMediaControlService);
  private toastr = inject(ToastrService);
  private homieService = inject(HomieService);

  private _callStore: CallStoreService | null = null;
  private get callStore(): CallStoreService {
    if (!this._callStore) {
      this._callStore = this.injector.get(CallStoreService);
    }
    return this._callStore;
  }

  private _broadcastService: BroadcastService | null = null;
  private get broadcastService(): BroadcastService {
    if (!this._broadcastService) {
      this._broadcastService = this.injector.get(BroadcastService);
    }
    return this._broadcastService;
  }

  async callInvite(
    invitees: string[],
    type: CallType = CALL_TYPE.AUDIO,
    extendedData = '',
  ): Promise<void> {
    const hasInviteesInCall = invitees.find(
      (inv) => inv === this.callStore.pendingInvitation()?.inviter,
    );
    if (this.callStore.pendingInvitation() && hasInviteesInCall) {
      this.callStore.callSessionStore.setCurrentCallState(CALL_STATE.ENDED);
      return;
    }

    this.callStore.isMeClickCall.set(true);
    try {
      const userInfo = getUserInfo();
      const config: ZIMCallInviteConfig = {
        mode: ZIMCallInvitationMode.Advanced,
        extendedData,
        timeout: 60,
      };
      const pushConfig: ZIMPushConfig = {
        resourcesID: 'appviettel_call',
        title: `Cuộc gọi đến từ ${userInfo.zegoId ?? ''}`,
        content: 'Push Notification',
        enableBadge: true,
        badgeIncrement: 1,
        threadID: '',
        payload: extendedData,
      };
      config.pushConfig = pushConfig;

      const result = await this.callStore.callZimService.callInvite(invitees, config);
      if (!result) {
        this.callStore.isMeClickCall.set(false);
        this.toastr.error('Có lỗi xảy ra khi khởi tạo cuộc gọi. Vui lòng thử lại.');
        return;
      }

      this.homieService.registerOutgoingCall(result.callID, invitees);

      const body = {
        isVideo: type === CALL_TYPE.VIDEO,
        roomId: result.callID,
        calleeId: invitees,
        callerId: userInfo.zegoId,
      };
      this.meetingManagementService.createCall(body).subscribe(() => {});
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      this.callStore.isMeClickCall.set(false);
      if (error.code) {
        switch (error.code) {
          case 6000321:
            throw new Error('Người dùng không tồn tại');
          case 6000322:
            throw new Error('Người dùng đang offline');
          case 6000323:
            throw new Error('Người dùng đang trong cuộc gọi khác');
          case 6000324:
            throw new Error('Người dùng đã có lời mời cuộc gọi đang chờ');
          default:
            throw new Error(
              `Không thể bắt đầu cuộc gọi: ${error.code} ${error.message || 'Lỗi không xác định'}`,
            );
        }
      }
      throw error;
    }
  }

  async callAccept(callId: string): Promise<void> {
    this.callStore.isMeClickAccept.set(true);
    this.callStore.callAudio.stop();

    if (this.callStore.callState() === CALL_STATE.CONNECTED) {
      await this.switchCall();
      return;
    }

    this.broadcastService.call({
      action: CALL_ACTION.ACCEPT_CALL,
      payload: { callId, isSwitchCall: false },
    });

    this.acceptIncomingCall();

    const config: ZIMCallAcceptConfig = { extendedData: JSON.stringify({ flatform: 'web' }) };
    try {
      await this.callStore.callZimService.callAccept(callId, config);
      const currentCall = this.callStore.currentCall();
      if (currentCall) this.callStore.publishCallStream(callId, currentCall.type);
    } catch (error) {
      this.logger.warn('callAccept error:', error);
      this.callStore.reset();
    }
  }

  acceptIncomingCall(isSwitchCall = false): void {
    const incoming = this.callStore.pendingInvitation();
    if (!incoming) return;

    this.callStore.callSessionStore.setCurrentCall({
      callId: incoming.callId,
      type: incoming.type,
      startTime: incoming.createTime || new Date(),
      inviter: incoming.inviter,
      extendedData: incoming.extendedData,
      isSwitchCall,
    });
    this.callStore.callSessionStore.setCurrentCallState(CALL_STATE.CONNECTING);
    this.callStore.callSessionStore.setParticipants(incoming.invitees);
    this.callStore.isMeClickAccept.set(false);
    this.callStore.callSessionStore.setPendingInvitation(null);
  }

  async switchCall(): Promise<void> {
    const waiting = this.callStore.pendingInvitation();
    const current = this.callStore.currentCall();
    if (!waiting || !current) return;

    this.broadcastService.call({
      action: CALL_ACTION.ACCEPT_CALL,
      payload: { callId: waiting.callId, isSwitchCall: true },
    });

    const switchData: SwitchCallExtendedData = {
      callID: current.callId,
      userId: this.callStore.zimService.userId ?? '',
      reason: 'switch-call',
    };
    const extendedData = JSON.stringify(switchData);

    if (current.inviter === this.callStore.zimService.userId) {
      await this.callStore.callZimService.callEnd(current.callId, { extendedData });
    } else {
      await this.callStore.callZimService.callQuit(current.callId, { extendedData });
    }

    if (this.callStore.isLoginRoom()) {
      this.callStore.callZegoService.logoutRoom(current.callId);
      this.callStore.isLoginRoom.set(false);
    }
    this.callStore.callZegoService.stopPubishAndDestroyStream(
      this.callStore.localStream(),
      this.callStore.localStreamId()!,
    );

    if (this.zegoMediaControlService.mediaState().isScreenSharing) {
      this.zegoMediaControlService.stopScreenShare();
    }
    await this.callStore.callZimService.callAccept(waiting.callId, { extendedData: '' });
    this.acceptIncomingCall(true);
    await this.callStore.publishCallStream(waiting.callId, waiting.type);
  }

  async callReject(callId: string, isRejectSwitchCall = false): Promise<void> {
    this.broadcastService.call({
      action: CALL_ACTION.REJECT_CALL,
      payload: { callId },
    });
    this.callStore.callSessionStore.setPendingInvitation(null);
    const config: ZIMCallRejectConfig = { extendedData: '' };
    try {
      await this.callStore.callZimService.callReject(callId, config);
    } catch (error) {
      this.logger.warn('callReject error:', error);
    }
    if (!isRejectSwitchCall) {
      this.callStore.handleCallInvitationError(callId, CALL_END_REASON.REJECTED);
    }
    if (this.callStore.callState() !== CALL_STATE.CONNECTED) {
      this.callStore.callSessionStore.setCurrentCallState(CALL_STATE.IDLE);
    }
  }

  async callEnd(): Promise<void> {
    const call = this.callStore.currentCall();
    if (!call) return;
    const config: ZIMCallEndConfig = { extendedData: '' };
    try {
      await this.callStore.callZimService.callEnd(call.callId, config);
    } catch (error) {
      this.logger.warn('callEnd error:', error);
    }
    this.callStore.handleCallInvitationError(call.callId, CALL_END_REASON.NORMAL);
  }

  async callQuit(callId: string): Promise<void> {
    const config: ZIMCallQuitConfig = { extendedData: '' };
    try {
      await this.callStore.callZimService.callQuit(callId, config);
    } catch (error) {
      this.logger.warn('callQuit error:', error);
    }
    this.callStore.handleCallInvitationError(callId, CALL_END_REASON.NORMAL);
  }

  async callCancel(invitees: string[]): Promise<void> {
    const call = this.callStore.currentCall();
    if (!call) return;
    const config: ZIMCallCancelConfig = { extendedData: '' };
    try {
      await this.callStore.callZimService.callCancel(invitees, call.callId, config);
    } catch (error) {
      this.logger.warn('callCancel error:', error);
    }
    this.callStore.handleCallInvitationError(call.callId, CALL_END_REASON.CANCELLED);
  }
}
